import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button } from '@shared/components/ui/button';
import { Input } from '@shared/components/ui/input';
import { Label } from '@shared/components/ui/label';
import { Textarea } from '@shared/components/ui/textarea';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@shared/components/ui/card';
import { Alert, AlertDescription } from '@shared/components/ui/alert';
import { Loader2, CheckCircle } from 'lucide-react';
import { usePortalConfig } from '@shared/config';
import { submitContactForm } from '@shared/services/contact.service';

const contactSchema = z.object({
  naam: z.string().min(2, 'Naam is verplicht'),
  email: z.string().email('Voer een geldig e-mailadres in'),
  onderwerp: z.string().min(3, 'Onderwerp is verplicht'),
  bericht: z.string().min(10, 'Bericht moet minimaal 10 tekens bevatten'),
});

type ContactFormData = z.infer<typeof contactSchema>;

export function ContactForm() {
  const { id: portalId } = usePortalConfig();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<ContactFormData>({
    resolver: zodResolver(contactSchema),
  });

  const onSubmit = async (data: ContactFormData) => {
    try {
      setIsSubmitting(true);
      setSubmitError(null);
      await submitContactForm({
        ...data,
        portalId,
      });
      setSubmitSuccess(true);
      reset();
    } catch (_err) {
      setSubmitError('Er is iets misgegaan bij het versturen van uw bericht. Probeer het later opnieuw.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitSuccess) {
    return (
      <Card className="w-full max-w-lg">
        <CardHeader>
          <div className="flex items-center gap-2">
            <CheckCircle className="h-6 w-6 text-green-500" />
            <CardTitle>Bericht verzonden</CardTitle>
          </div>
          <CardDescription>
            Bedankt voor uw bericht. We nemen zo snel mogelijk contact met u op.
          </CardDescription>
        </CardHeader>
        <CardFooter>
          <Button
            variant="outline"
            onClick={() => setSubmitSuccess(false)}
            className="w-full"
          >
            Nieuw bericht versturen
          </Button>
        </CardFooter>
      </Card>
    );
  }

  return (
    <Card className="w-full max-w-lg">
      <CardHeader>
        <CardTitle>Contact</CardTitle>
        <CardDescription>
          Heeft u een vraag of opmerking? Neem contact met ons op via onderstaand formulier.
        </CardDescription>
      </CardHeader>
      <form onSubmit={handleSubmit(onSubmit)}>
        <CardContent className="space-y-4">
          {submitError && (
            <Alert variant="destructive">
              <AlertDescription>{submitError}</AlertDescription>
            </Alert>
          )}

          <div className="space-y-2">
            <Label htmlFor="naam">Naam</Label>
            <Input
              id="naam"
              placeholder="Uw naam"
              {...register('naam')}
              disabled={isSubmitting}
            />
            {errors.naam && (
              <p className="text-sm text-destructive">{errors.naam.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="email">E-mailadres</Label>
            <Input
              id="email"
              type="email"
              placeholder="naam@voorbeeld.nl"
              {...register('email')}
              disabled={isSubmitting}
            />
            {errors.email && (
              <p className="text-sm text-destructive">{errors.email.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="onderwerp">Onderwerp</Label>
            <Input
              id="onderwerp"
              placeholder="Waar gaat uw vraag over?"
              {...register('onderwerp')}
              disabled={isSubmitting}
            />
            {errors.onderwerp && (
              <p className="text-sm text-destructive">{errors.onderwerp.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="bericht">Bericht</Label>
            <Textarea
              id="bericht"
              placeholder="Uw bericht..."
              {...register('bericht')}
              disabled={isSubmitting}
            />
            {errors.bericht && (
              <p className="text-sm text-destructive">{errors.bericht.message}</p>
            )}
          </div>
        </CardContent>
        <CardFooter className="pt-6">
          <Button
            type="submit"
            className="w-full"
            disabled={isSubmitting}
          >
            {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {isSubmitting ? 'Verzenden...' : 'Verstuur bericht'}
          </Button>
        </CardFooter>
      </form>
    </Card>
  );
}
